import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
} from '@mui/material';
import {
  ShoppingCart as ShoppingCartIcon,
  Business as BusinessIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  Inventory as InventoryIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { notaEntradaService, NotaEntrada } from '../../services/notaEntrada';

interface NotaEntradaVisualizarProps {
  id: number;
}

const NotaEntradaVisualizar: React.FC<NotaEntradaVisualizarProps> = ({ id }) => {
  const navigate = useNavigate();

  const { data: notaEntrada, isLoading, error } = useQuery({
    queryKey: ['notaEntrada', id],
    queryFn: () => notaEntradaService.getById(id),
    enabled: !!id,
  });

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Typography>Carregando...</Typography>
      </Box>
    );
  }

  if (error || !notaEntrada) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Typography color="error">Erro ao carregar a nota de entrada.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Header */}
      <Paper sx={{ p: 3, backgroundColor: 'primary.main', color: 'white' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1 }}>
              Nota de Entrada #{notaEntrada.id}
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Fornecedor: {notaEntrada.nomeFornecedor || notaEntrada.fornecedor || '-'}
            </Typography>
          </Box>
          <Box>
            <Chip
              label="Visualização"
              color="secondary"
              variant="outlined"
              sx={{ 
                borderColor: 'white', 
                color: 'white',
                '& .MuiChip-label': { color: 'white' }
              }}
            />
          </Box>
        </Box>
      </Paper>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
        {/* Dados Principais */}
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              <ShoppingCartIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Dados da Nota
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Código
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                  #{notaEntrada.id}
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Fornecedor
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                  {notaEntrada.nomeFornecedor || notaEntrada.fornecedor || '-'}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Datas */}
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              <CalendarIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Datas
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Data da Compra
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                  {notaEntrada.dataCompraString}
                </Typography>
              </Box>
              
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Data de Inclusão
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                  {notaEntrada.dataInclusaoString}
                </Typography>
              </Box>

              {notaEntrada.dataEntradaEmEstoqueString && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Data de Entrada em Estoque
                  </Typography>
                  <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                    {notaEntrada.dataEntradaEmEstoqueString}
                  </Typography>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Valores */}
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              <MoneyIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Valores
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Valor Total
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                  {formatCurrency(notaEntrada.valor)}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Produtos */}
      <Card variant="outlined">
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
            <InventoryIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Produtos da Nota
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 'bold' }}>Referência</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Produto</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Quantidade</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Valor Unitário</TableCell>
                  <TableCell sx={{ fontWeight: 'bold' }}>Valor Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {notaEntrada.produtos && notaEntrada.produtos.length > 0 ? (
                  notaEntrada.produtos.map((produto) => (
                    <TableRow key={produto.id}>
                      <TableCell sx={{ fontFamily: 'monospace' }}>
                        {produto.referencia}
                      </TableCell>
                      <TableCell>{produto.nomeProduto}</TableCell>
                      <TableCell>{produto.quantidade}</TableCell>
                      <TableCell>{formatCurrency(produto.precoUnitario)}</TableCell>
                      <TableCell sx={{ fontWeight: 'bold' }}>
                        {formatCurrency(produto.valorTotal ?? produto.totalProduto ?? produto.quantidade * produto.precoUnitario)}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      <Typography sx={{ py: 4, color: 'text.secondary' }}>
                        Nenhum produto encontrado nesta nota de entrada.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Botão Voltar */}
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/entradas')}
          sx={{ minWidth: 150 }}
        >
          Voltar para Lista
        </Button>
      </Box>
    </Box>
  );
};

export default NotaEntradaVisualizar;
